import { Camera, ChevronRight } from "lucide-react";
import { Fragment, type ReactNode } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { MaquinariaDeInforme } from "@/lib/informes";
import { formatearAjuste, formatearCifra, formatearMonto, formatearVidaUtil } from "@/lib/montos";
import { useResaltadoDeSincronizaciones } from "@/hooks/useResaltadoDeSincronizaciones";
import { cn } from "@/lib/utils";

import { conteoDeRegistrosDelItem } from "./aguasTerreno";
import { FilaDeTerrenoDeLaFila } from "./FilaDeTerrenoDeLaFila";
import { marcaDeFila } from "./MarcasDeSincronizacion";
import {
  claveDeMaquina,
  claveDeTerreno,
  creadaEnTerrenoSinValorizar,
  itemDeLaMaquina,
  opcionesParaVincular,
  type ItemDeMaquinaPorEnlazar,
  type TerrenoDeLaLista,
} from "./maquinariasTerreno";

interface TablaDeMaquinariasProps {
  filas: MaquinariaDeInforme[];
  /** TODAS las guardadas, no solo las visibles: "Es la misma que" no depende del filtro. */
  todas?: MaquinariaDeInforme[];
  ocupada: boolean;
  onAbrir: (maquina: MaquinariaDeInforme) => void;
  onValidar: (maquina: MaquinariaDeInforme, validada: boolean) => void;
  /** Lo que el encargado dejó en el predio; sin él la tabla es la de siempre. */
  terreno?: TerrenoDeLaLista;
  /** Qué registros de terreno están desplegados, por clave de fila. */
  abiertas?: ReadonlySet<string>;
  onAlternar?: (clave: string) => void;
  /** Completar una máquina de terreno: abre el formulario de alta precargado. */
  onAdoptar?: (porEnlazar: ItemDeMaquinaPorEnlazar) => void;
}

function Cifra({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("font-mono text-xs font-semibold", className)}>{children}</span>;
}

/** `"48000.00"` → `48000`. Los decimales llegan como string del backend. */
function numeroDe(valor: string | null): number | null {
  if (valor === null || valor.trim() === "") return null;

  const numero = Number(valor);

  return Number.isFinite(numero) ? numero : null;
}

/**
 * Los dos valores unitarios de la fila, "US$ 48.000 · 29.000".
 *
 * LA MONEDA VA PEGADA A LA PRIMERA CIFRA y no se repite en la segunda: cada
 * fila elige la suya, así que una cifra desnuda no diría cuánto vale — pero las
 * dos comparten moneda y repetirla ensancharía una columna que ya es la más
 * ancha. "UNIT." en el encabezado por lo mismo que siempre: al lado hay una
 * columna que SÍ es un total, y confundirlos erra por un factor entero sin que
 * ninguna cifra se vea rara.
 */
function valoresUnitarios(maquina: MaquinariaDeInforme): string {
  const reposicion = numeroDe(maquina.valor_unitario_reposicion);
  const residual = numeroDe(maquina.valor_unitario_residual);

  if (reposicion === null && residual === null) return "—";

  return `${formatearMonto(reposicion, maquina.moneda)} · ${formatearCifra(maquina.valor_unitario_residual)}`;
}

/**
 * La tabla de la lista: una fila por máquina, hecha con el `Table` del DS.
 *
 * LA FILA ENTERA ABRE EL MODAL, y el nombre es además un botón: un `onClick`
 * en un `<tr>` no lo alcanza el teclado. La casilla Valid. frena la
 * propagación —marcar no puede abrir el detalle—.
 */
export function TablaDeMaquinarias({
  filas,
  todas = filas,
  ocupada,
  onAbrir,
  onValidar,
  terreno,
  abiertas,
  onAlternar,
  onAdoptar,
}: TablaDeMaquinariasProps) {
  const alinear = "text-right";
  const opciones = opcionesParaVincular(todas);
  const resaltado = useResaltadoDeSincronizaciones();

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead variant="versalitas" className="pl-5">Máquina o equipo</TableHead>
          <TableHead variant="versalitas" className={alinear}>Año</TableHead>
          <TableHead variant="versalitas" className={alinear}>Unid.</TableHead>
          <TableHead variant="versalitas" className={alinear}>Vida T/R</TableHead>
          <TableHead variant="versalitas" className={alinear}>V. unit. rep · res</TableHead>
          <TableHead variant="versalitas" className={alinear}>Ajuste</TableHead>
          <TableHead variant="versalitas" className={alinear}>Valor comercial</TableHead>
          <TableHead variant="versalitas">Fotos</TableHead>
          <TableHead variant="versalitas" className="text-center">Valid.</TableHead>
          <TableHead className="w-7 pr-3.5">
            <span className="sr-only">Abrir</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {filas.map((maquina) => {
          const ajuste = maquina.ajuste_comercial_pct;
          const ajusteNumero = ajuste === null ? 0 : Number(ajuste);
          const fotos = maquina.fotos?.length ?? 0;

          const clave = claveDeMaquina(maquina.id);
          const marca = marcaDeFila(resaltado.deItem(terreno ? itemDeLaMaquina(terreno.items, maquina) : null));

          return (
            <Fragment key={maquina.id}>
              <TableRow
                id={clave}
                onClick={() => onAbrir(maquina)}
                {...marca.atributo}
                className={cn("h-10 cursor-pointer", marca.claseFila)}
              >
                <TableCell className={cn("max-w-[20rem] pl-5", marca.claseCelda)}>
                  <div className="flex min-w-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAbrir(maquina);
                    }}
                    className="text-ink focus-visible:ring-ring/50 min-w-0 max-w-full truncate rounded-sm text-[0.78125rem] font-bold outline-none focus-visible:ring-[3px]"
                  >
                    {maquina.nombre}
                  </button>
                  {marca.tag}
                </div>
                </TableCell>

                <TableCell className={alinear}>
                  <Cifra>{maquina.anio_fabricacion ?? "—"}</Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  <Cifra className="font-bold">{maquina.unidades}</Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  <Cifra className={maquina.vida_util_total === null ? "text-ink-ghost" : undefined}>
                    {formatearVidaUtil(maquina.vida_util_total, maquina.vida_util_residual)}
                  </Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  <Cifra
                    className={
                      maquina.valor_unitario_reposicion === null &&
                      maquina.valor_unitario_residual === null
                        ? "text-ink-ghost"
                        : "whitespace-nowrap"
                    }
                  >
                    {valoresUnitarios(maquina)}
                  </Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  <Cifra
                    className={
                      ajusteNumero < 0
                        ? "text-danger-soft-foreground"
                        : ajusteNumero > 0
                          ? "text-success-soft-foreground"
                          : "text-ink-ghost"
                    }
                  >
                    {ajusteNumero === 0 ? "—" : formatearAjuste(ajuste)}
                  </Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  {maquina.valor_comercial_total === null ? (
                    <span className="bg-warning-soft text-warning-soft-foreground rounded-sm px-1.5 py-0.5 text-[0.65625rem] font-bold">
                      Sin valorizar
                    </span>
                  ) : (
                    // EL ÚNICO TOTAL DE LA FILA, y por eso lleva el símbolo de su
                    // moneda pegado: es la cifra que se lee sola.
                    <span className="font-mono text-[0.78125rem] font-bold whitespace-nowrap">
                      {formatearMonto(maquina.valor_comercial_total, maquina.moneda)}
                    </span>
                  )}
                </TableCell>

                <TableCell>
                  <span
                    aria-label={fotos === 1 ? "1 foto" : `${fotos} fotos`}
                    className={cn(
                      "inline-flex items-center gap-1",
                      fotos === 0 ? "text-ink-ghost" : "text-ink-muted",
                    )}
                  >
                    <Camera className="size-3" aria-hidden="true" />
                    <span className="font-mono text-[0.6875rem] font-bold">{fotos}</span>
                  </span>
                </TableCell>

                <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    aria-label={`Validar ${maquina.nombre}`}
                    checked={maquina.validada}
                    disabled={ocupada}
                    title={maquina.validada ? "Quitar validación" : "Marcar los datos como revisados"}
                    // SE GUARDA SOLA y viaja sin el resto de los campos, para no
                    // pisar lo que otro pudo haber corregido mientras tanto.
                    onCheckedChange={(v) => onValidar(maquina, v === true)}
                  />
                </TableCell>

                <TableCell className="pr-3.5 text-right">
                  <ChevronRight className="text-ink-ghost inline size-3.5" aria-hidden="true" />
                </TableCell>
              </TableRow>
            {terreno ? (
              <FilaDeTerrenoDeLaFila
                terreno={terreno}
                titulo={maquina.nombre}
                item={itemDeLaMaquina(terreno.items, maquina)}
                deTerreno={maquina.terreno_ref !== null}
                opciones={opciones}
                placeholderDelVinculo="Elegir máquina…"
                abierto={abiertas?.has(clave) ?? false}
                ocupada={ocupada}
                onAlternar={() => onAlternar?.(clave)}
                guardada={{
                  id: maquina.id,
                  fotos,
                  ausenciaMantenidaEn: maquina.ausencia_mantenida_en ?? null,
                }}
                queEs="máquina"
                esta="esta"
                creadaSinValorizar={creadaEnTerrenoSinValorizar(maquina)}
              />
            ) : null}
            </Fragment>
          );
        })}

        {terreno?.porEnlazar.map((porEnlazar) => {
          const { uuid, item } = porEnlazar;
          const clave = claveDeTerreno(uuid);
          const marca = marcaDeFila(resaltado.deItem(item));
          const fotos = conteoDeRegistrosDelItem(item).fotos;

          return (
            <Fragment key={uuid}>
              <TableRow id={clave} onClick={() => onAdoptar?.(porEnlazar)} {...marca.atributo}
              className={cn("h-10 cursor-pointer", marca.claseFila)}>
                <TableCell className={cn("max-w-[20rem] pl-5", marca.claseCelda)}>
                  <div className="flex min-w-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAdoptar?.(porEnlazar);
                    }}
                    className="text-ink focus-visible:ring-ring/50 min-w-0 max-w-full truncate rounded-sm text-[0.78125rem] font-bold outline-none focus-visible:ring-[3px]"
                  >
                    {item.nombre}
                  </button>
                  {marca.tag}
                </div>
                </TableCell>
                <TableCell className={alinear}>
                  <Cifra className={item.anio_fabricacion == null ? "text-ink-ghost" : undefined}>
                    {item.anio_fabricacion ?? "—"}
                  </Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  <Cifra className="font-bold">{item.unidades ?? "—"}</Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  <Cifra className="text-ink-ghost">—</Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  <Cifra className="text-ink-ghost">—</Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  <Cifra className="text-ink-ghost">—</Cifra>
                </TableCell>
                <TableCell className={alinear}>
                  <span className="bg-warning-soft text-warning-soft-foreground rounded-sm px-1.5 py-0.5 text-[0.65625rem] font-bold">
                    Sin valorizar
                  </span>
                </TableCell>
                <TableCell>
                  <span
                    aria-label={fotos === 1 ? "1 foto" : `${fotos} fotos`}
                    className={cn("inline-flex items-center gap-1", fotos === 0 ? "text-ink-ghost" : "text-ink-muted")}
                  >
                    <Camera className="size-3" aria-hidden="true" />
                    <span className="font-mono text-[0.6875rem] font-bold">{fotos}</span>
                  </span>
                </TableCell>
                <TableCell />
                <TableCell className="pr-3.5 text-right">
                  <ChevronRight className="text-ink-ghost inline size-3.5" aria-hidden="true" />
                </TableCell>
              </TableRow>
              <FilaDeTerrenoDeLaFila
                terreno={terreno}
                titulo={item.nombre}
                item={item}
                deTerreno
                uuidPorEnlazar={uuid}
                creadaSinValorizar
                opciones={opciones}
                placeholderDelVinculo="Elegir máquina…"
                abierto={abiertas?.has(clave) ?? false}
                ocupada={ocupada}
                onAlternar={() => onAlternar?.(clave)}
              />
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
